/**
 * Odoo ERP MCP integration — request validators.
 */
import { z } from 'zod';
export const OdooConfigUpdateSchema = z
    .object({
    enabled: z.boolean(),
    baseUrl: z.string().url().max(500),
    database: z.string().min(1).max(200),
    serviceAccountUsername: z.string().min(1).max(200),
    /** Plaintext — only present when the admin is setting/replacing the key. */
    apiKey: z.string().min(1).max(500),
    allowUnlink: z.boolean(),
})
    .partial()
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
// ----------------------------------------------------------------------------
// Permission grants — v2.4.0 PR 2.
// ----------------------------------------------------------------------------
/** Odoo technical model names are lowercase dotted identifiers, e.g. "res.partner". */
export const OdooModelNameSchema = z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/, 'Must be an Odoo technical model name, e.g. res.partner');
/** Odoo method names: identifiers, optionally private (leading underscore allowed). */
export const OdooMethodNameSchema = z
    .string()
    .min(1)
    .max(100)
    .regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/, 'Must be a valid method name, e.g. action_confirm');
/** The fine-grained capability set for one model grant (v2.4.1). */
export const OdooModelCapabilitiesSchema = z.object({
    canRead: z.boolean(),
    canCreate: z.boolean(),
    canUpdate: z.boolean(),
    canDelete: z.boolean(),
    canCall: z.boolean(),
    allowedMethods: z.array(OdooMethodNameSchema).max(100).default([]),
});
export const GrantOdooModelAccessSchema = OdooModelCapabilitiesSchema.extend({
    odooModel: OdooModelNameSchema,
});
/**
 * Bulk grant — apply one capability set to many models at once, or to EVERY
 * model in the instance (`allModels: true`, resolved server-side from ir.model).
 * Exactly one of `odooModels` / `allModels` is required.
 */
export const BulkGrantOdooModelAccessSchema = OdooModelCapabilitiesSchema.extend({
    odooModels: z.array(OdooModelNameSchema).max(2000).optional(),
    allModels: z.boolean().optional(),
}).refine((d) => d.allModels === true || (d.odooModels !== undefined && d.odooModels.length > 0), {
    message: 'Provide odooModels or set allModels: true',
});
export const UpdateOdooModelAccessSchema = OdooModelCapabilitiesSchema.partial().refine((d) => Object.keys(d).length > 0, { message: 'At least one field must be provided' });
// ----------------------------------------------------------------------------
// MCP tool audit log query — v2.4.0 PR 3.
// ----------------------------------------------------------------------------
/** Filters + cursor for GET /admin/odoo/logs (mirrors audit.routes' cursor shape). */
export const OdooLogsQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    before: z.coerce.date().optional(),
    userId: z.string().uuid().optional(),
    odooModel: z.string().max(100).optional(),
    toolName: z.string().max(80).optional(),
    action: z.enum(['read', 'write', 'admin']).optional(),
    errorOnly: z.coerce.boolean().optional(),
});
// ----------------------------------------------------------------------------
// Odoo AI Agent config — v2.4.0 PR 4.
// ----------------------------------------------------------------------------
export const OdooAgentConfigUpdateSchema = z
    .object({
    defaultModelsHint: z.array(OdooModelNameSchema).max(50),
    confirmBeforeWrite: z.boolean(),
    confirmBeforeUnlink: z.boolean(),
    exportFormats: z.array(z.enum(['pdf', 'csv', 'json', 'excel', 'docs'])).max(5),
})
    .partial()
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
// ----------------------------------------------------------------------------
// Odoo chat surface — v2.4.0 PR 5.
// ----------------------------------------------------------------------------
/** A single chat turn's user message. Bounded to keep prompt sizes sane. */
export const OdooChatSendSchema = z.object({
    message: z.string().trim().min(1).max(8000),
});
/** Body for resolving a previewed change via the Confirm/Reject buttons. */
export const ResolveOdooPendingActionSchema = z.object({
    decision: z.enum(['confirm', 'reject']),
});
// ----------------------------------------------------------------------------
// UST-3143 — Odoo Helpdesk sync.
// ----------------------------------------------------------------------------
const HELPDESK_STATUSES = ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'];
export const OdooHelpdeskUpdateSchema = z
    .object({
    enabled: z.boolean(),
    /** Odoo stage id → our status. Only the stages sent are changed. */
    stageMap: z.record(z.string().regex(/^\d+$/), z.enum(HELPDESK_STATUSES)),
})
    .partial()
    .refine((d) => Object.keys(d).length > 0, {
    message: 'At least one field must be provided',
});
/** POST /support/tickets/:ticketId/odoo — send a ticket to an Odoo Helpdesk team. */
export const SupportTicketOdooExportSchema = z.object({
    teamId: z.number().int().positive(),
});
//# sourceMappingURL=odoo.schema.js.map